import { toast } from "@carbon6/c6-frontend-design";
import { useEffect, useState } from "react";
import { getNote, saveNote, type NoteEntityType, type NoteView } from "@/api";
import { createLogger } from "@/logging";

const logger = createLogger("components/note");

export type NoteLoadState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "success"; data: NoteView }
  | { status: "error"; message: string };

export type NoteController = {
  state: NoteLoadState;
  saving: boolean;
  reload: () => void;
  // 저장이 성공하면 true 다. 빈/공백 텍스트는 저장하지 않는다(삭제는 onClear).
  onSave: (text: string) => Promise<boolean>;
  // 지우기가 성공하면 true 다. 실패하면 false 이고 편집 draft를 유지한다.
  onClear: () => Promise<boolean>;
};

export type UseNoteControllerOptions = {
  // false 면 fetch 하지 않는다. 비활성 표면(닫힌 탭)에서 불필요한 요청을 막는다(기본 true).
  enabled?: boolean;
};

// `(entity_type, entity_id)`별 단일 노트의 조회·저장·삭제를 소유한다. 노트 본문을 저장할 때
// 로드한 tags를 그대로 포함해 whole-resource PUT한다. 액션 실패는 toast로 알린다.
export function useNoteController(
  entityType: NoteEntityType,
  entityId: string,
  options: UseNoteControllerOptions = {},
): NoteController {
  const enabled = options.enabled ?? true;
  const [state, setState] = useState<NoteLoadState>({ status: "idle" });
  const [saving, setSaving] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });
    void getNote(entityType, entityId)
      .then((view) => {
        if (!cancelled) setState({ status: "success", data: view });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        logger.error("getNote failed", error);
        setState({ status: "error", message: "노트를 불러오지 못했습니다." });
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, entityType, entityId, refreshKey]);

  // 빈 본문 저장을 여기서 막지 않는다. 본문을 비우는 wire 연산은 saveNote 가 소유한다. 저장 버튼
  // 비활성(빈 본문으로는 누를 수 없음)은 NotePanel 이 UI 게이트로 처리한다.
  async function handleSave(text: string): Promise<boolean> {
    if (saving || state.status !== "success") {
      return false;
    }
    setSaving(true);
    try {
      const updated = await saveNote(entityType, entityId, {
        note: text,
        tags: state.data.tags,
      });
      setState({ status: "success", data: updated });
      setSaving(false);
      return true;
    } catch (error) {
      logger.error("saveNote failed", error);
      toast.error("노트 저장에 실패했습니다.");
      setSaving(false);
      return false;
    }
  }

  // "지우기" 는 본문을 비우는 동작이다. 태그가 남아 있으면 레코드는 지우지 않는다. 이 화면에는
  // 태그 UI 가 없어서, 레코드를 지우면 사용자가 모르는 태그를 잃는다. 판정은 saveNote 가 한다.
  async function handleClear(): Promise<boolean> {
    if (saving || state.status !== "success") {
      return false;
    }
    setSaving(true);
    try {
      const cleared = await saveNote(entityType, entityId, {
        note: "",
        tags: state.data.tags,
      });
      setState({ status: "success", data: cleared });
      setSaving(false);
      return true;
    } catch (error) {
      logger.error("saveNote(clear) failed", error);
      toast.error("노트 삭제에 실패했습니다.");
      setSaving(false);
      return false;
    }
  }

  return {
    state,
    saving,
    reload: () => setRefreshKey((key) => key + 1),
    onSave: handleSave,
    onClear: handleClear,
  };
}
